import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { ResponsiveLine } from "@nivo/line";

const LINE_COLOR = "#f5751D"; // brand orange
const LINE_COLOR_DARK = "#ffa450";

function CallsByHourLine({ records = [], isDark = false }) {
  const { series, total } = useMemo(() => {
    const buckets = Array.from({ length: 24 }, (_, h) => ({
      x: String(h).padStart(2, "0"),
      y: 0,
    }));
    let sum = 0;
    for (const r of records) {
      const ts = r?.start_at;
      if (!ts) continue;
      const d = new Date(ts);
      const h = d.getHours();
      if (Number.isFinite(h) && h >= 0 && h < 24) {
        buckets[h].y += 1;
        sum += 1;
      }
    }
    return { series: [{ id: "Calls", data: buckets }], total: sum };
  }, [records]);

  if (total === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography variant="body2" sx={{ color: isDark ? "#8899aa" : "#999" }}>
          No call data available for today
        </Typography>
      </Box>
    );
  }

  const axisColor = isDark ? "#b0bec5" : "#555";
  const gridColor = isDark ? "#2a3a4a" : "#e5e7eb";
  const lineColor = isDark ? LINE_COLOR_DARK : LINE_COLOR;

  return (
    <Box
      sx={{
        height: "320px",
        // Line draws itself left-to-right via the classic stroke-dasharray /
        // stroke-dashoffset trick. We target Nivo's actual line path with
        // path[fill="none"][stroke] — the area path has fill and no stroke,
        // so the selector reliably hits the line and only the line.
        "& path[fill='none'][stroke]": {
          strokeDasharray: 2800,
          strokeDashoffset: 2800,
          animation: "cbhLineDraw 2.4s cubic-bezier(0.4, 0, 0.2, 1) forwards",
        },
        // Area gradient fill + the data points fade in AFTER the line
        // finishes drawing — keeps the reveal sequenced rather than the
        // line racing against an already-painted area.
        "& g > path[fill]:not([fill='none']), & circle": {
          opacity: 0,
          animation: "cbhLineFade 0.5s ease-out 2.1s forwards",
        },
        "@keyframes cbhLineDraw": {
          to: { strokeDashoffset: 0 },
        },
        "@keyframes cbhLineFade": {
          to: { opacity: 1 },
        },
      }}
    >
      <ResponsiveLine
        data={series}
        margin={{ top: 20, right: 20, bottom: 50, left: 45 }}
        xScale={{ type: "point" }}
        yScale={{ type: "linear", min: 0, max: "auto", stacked: false }}
        curve="monotoneX"
        colors={[lineColor]}
        lineWidth={5}
        // Solid filled circles in the line color — gives the chart a chunkier
        // "data-point" feel rather than the hollow donut look. Thin white
        // outer ring keeps each point legible against the area fill.
        pointSize={9}
        pointColor={{ from: "serieColor" }}
        pointBorderWidth={2}
        pointBorderColor="#ffffff"
        useMesh
        // Hour labels every 3 hours keeps the x-axis readable; the mesh
        // tooltip still resolves to the exact hovered hour.
        axisBottom={{
          tickSize: 0,
          tickPadding: 8,
          tickValues: ["00", "03", "06", "09", "12", "15", "18", "21"],
          legend: "Hour of day",
          legendOffset: 38,
          legendPosition: "middle",
        }}
        axisLeft={{
          tickSize: 0,
          tickPadding: 6,
          tickValues: 5,
          legend: "Calls",
          legendOffset: -38,
          legendPosition: "middle",
        }}
        enableArea
        // Soft area fill below the line — hints at "volume" without
        // overwhelming the line itself.
        areaOpacity={0.18}
        defs={[
          {
            id: "callsAreaGrad",
            type: "linearGradient",
            colors: [
              { offset: 0, color: lineColor, opacity: 0.4 },
              { offset: 100, color: lineColor, opacity: 0 },
            ],
          },
        ]}
        fill={[{ match: "*", id: "callsAreaGrad" }]}
        enableGridY
        enableGridX={false}
        gridYValues={5}
        theme={{
          axis: {
            ticks: { text: { fill: axisColor, fontSize: 11 } },
            legend: {
              text: { fill: axisColor, fontSize: 12, fontWeight: 600 },
            },
          },
          grid: { line: { stroke: gridColor, strokeWidth: 1 } },
          crosshair: {
            line: { stroke: lineColor, strokeWidth: 1, strokeOpacity: 0.5 },
          },
        }}
        tooltip={({ point }) => (
          <Box
            sx={{
              background: isDark ? "#1a2332" : "#fff",
              color: `${isDark ? "#e0e0e0" : "#333"} !important`,
              px: 1.5,
              py: 0.9,
              borderRadius: 1.2,
              border: isDark ? "1px solid #2a3a4a" : "1px solid #e5e7eb",
              boxShadow: `0 8px 20px ${lineColor}55, 0 2px 8px rgba(0,0,0,0.18)`,
              fontSize: "0.85rem",
              fontWeight: 600,
            }}
          >
            <strong style={{ color: lineColor }}>
              {point.data.x}:00 – {point.data.x}:59
            </strong>
            {" · "}
            {point.data.y} call{point.data.y === 1 ? "" : "s"}
          </Box>
        )}
        motionConfig="gentle"
      />
    </Box>
  );
}

export default CallsByHourLine;
